import { useMutation, useQueryClient } from "@tanstack/react-query"
import { saveSupplierChanges, changeSupplierActive, sendNotificationEmail, updateClientStatus } from "../services/suppliersProfile.service"
import Swal from "sweetalert2"
import { useAuth } from "../../../../context/auth-user"

export const useSupplierProfileMutations = ({ id }) => {
	const queryClient = useQueryClient()
	const { user } = useAuth()
	const userLogged = user?.selectedProfile

	const saveChangesMutation = useMutation({
		mutationFn: saveSupplierChanges,
		onMutate: () => {
			Swal.fire({
				toast: true,
				footer: 'Loading, please wait',
				showConfirmButton: false,
				position: "bottom-end",
				didOpen: () => {
					Swal.showLoading()
				},
			})
		},
		onSuccess: () => {
			Swal.fire({
				toast: true,
				icon: 'success',
				text: 'Information saved successfully',
				showConfirmButton: false,
				timer: 2000,
				timerProgressBar: true,
				position: "bottom-end",
			})
			queryClient.invalidateQueries({ queryKey: ['supplier-supplierProfile', id] })
		},
		onError: (err) => {
			Swal.fire({
				toast: true,
				icon: 'error',
				text: 'Failed to save information',
				showConfirmButton: false,
				timer: 3000,
				timerProgressBar: true,
				position: "bottom-end"
			})
			console.log("Error al guardar la informacion del supplier: " + err)
		},
	})

	const handleSaveChanges = async (updatedSupplier) => {
		return await saveChangesMutation.mutateAsync({ supplier: updatedSupplier, id })
	}

	const statusChangeMutation = useMutation({
		mutationFn: ({ clientID, newStatus }) =>
			updateClientStatus({ id, clientID, newStatus, username: userLogged?.username, dataID: userLogged?.dataID, rol: userLogged?.rol }),
		onMutate: () => {
			Swal.fire({
				toast: true,
				footer: 'Loading, please wait',
				showConfirmButton: false,
				position: "bottom-end",
				didOpen: () => {
					Swal.showLoading()
				},
			})
		},
		onSuccess: () => {
			Swal.fire({
				toast: true,
				icon: 'success',
				text: 'Status updated successfully',
				showConfirmButton: false,
				timer: 2000,
				timerProgressBar: true,
				position: "bottom-end",
			})
			queryClient.invalidateQueries({ queryKey: ['supplier-supplierProfile', id] })
		},
		onError: (err) => {
			Swal.fire({
				toast: true,
				icon: 'error',
				text: 'Failed to update status',
				showConfirmButton: false,
				timer: 3000,
				timerProgressBar: true,
				position: "bottom-end"
			})
			console.log("Error al cambiar el status del supplier: " + err)
		},
	})

	const handleStatusChange = (clientID, newStatus) => {
		return statusChangeMutation.mutateAsync({ clientID, newStatus })
	}

	const changeActiveMutation = useMutation({
		mutationFn: changeSupplierActive,
		onSuccess: (data) => {
			if (data.errorMessage) {
				Swal.fire({
					toast: true,
					icon: 'error',
					text: `${data.errorMessage}`,
					showConfirmButton: false,
					timer: 3000,
					timerProgressBar: true,
					position: "bottom-end"
				})
			} else {
				Swal.fire({
					toast: true,
					icon: 'success',
					text: `${data.answer}`,
					showConfirmButton: false,
					timer: 2000,
					timerProgressBar: true,
					position: "bottom-end",
				})
				queryClient.invalidateQueries({ queryKey: ['supplier-supplierProfile', id] })
			}
		},
		onError: () => {
			Swal.fire({
				toast: true,
				icon: 'error',
				text: 'Failed to save information',
				showConfirmButton: false,
				timer: 3000,
				timerProgressBar: true,
				position: "bottom-end"
			})
		},
	})

	const handleChangeSupplierActive = (supplier) => {
		Swal.fire({
			title: `Are you sure you want to ${supplier.activeSupplier ? "disable" : "activate"} this supplier?`,
			icon: "warning",
			showCancelButton: true,
			confirmButtonText: "Yes, please!",
		}).then((result) => {
			if (result.isConfirmed) {
				changeActiveMutation.mutate({ supplierID: id, activeSupplier: supplier.activeSupplier })
			}
		})
	}

	const sendNotificationMutation = useMutation({
		mutationFn: sendNotificationEmail,
		onSuccess: () => {
			Swal.fire({
				toast: true,
				icon: 'success',
				text: 'Email sent',
				showConfirmButton: false,
				timer: 2000,
				timerProgressBar: true,
				position: "bottom-end"
			})
		},
		onError: () => {
			console.log("Error al enviar correo")
		},
	})

	const handleSendNotification = (email) => {
		Swal.fire({
			title: "Send email to supplier?",
			icon: "?",
			confirmButtonText: "Send email"
		}).then((result) => {
			if (result.isConfirmed) {
				sendNotificationMutation.mutate({ email })
			}
		})
	}

	return {
		handleSaveChanges,
		handleChangeSupplierActive,
		handleSendNotification,
		handleStatusChange,
	}
}
